import React, { useState, useRef, useEffect, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { Loader, Text, Box } from '@mantine/core';
import { useMutation, useQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { GET_IMPORT_STATUS } from '../../apollo/queries/getImportStatus';
import { ImportType } from '../../types/import';
import { GET_UPLOAD_URL } from '../../apollo/mutations/getUploadUrl';
import { PROCESS_IMPORT } from '../../apollo/mutations/processImport';
import { uploadFile } from '../../services/dataImport.service';
import { DateSelectionModal } from '../modals/DateSelectionModal';

const fileTypes = ['XLSX', 'XLS', 'CSV'];

interface DragDropProps {
    readonly importType: ImportType;
    readonly title: string;
    readonly description: string;
    readonly disabled?: boolean;
    readonly disabledReason?: string;
    readonly label?: string;
    readonly onScheduled?: () => void;
    readonly onUploadComplete?: () => void;
}

interface ImportLogResult {
    id: string;
    status: string;
    error?: string | null;
    recordsProcessed?: number | null;
    startedAt?: string | null;
    completedAt?: string | null;
    metadata?: Record<string, unknown> | null;
}

type GetUploadUrlResponse =
    | {
          getManualImportUploadUrl: {
              url: string;
              key: string;
          };
      }
    | undefined;

type ProcessImportResponse =
    | {
          processManualImport: {
              logId: string;
          };
      }
    | undefined;

export function DragDrop({
    importType,
    title,
    description,
    disabled = false,
    disabledReason,
    label,
    onScheduled,
    onUploadComplete,
}: DragDropProps): JSX.Element {
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [logId, setLogId] = useState<string | null>(null);
    const [isDateModalOpen, setIsDateModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const isHistoricalOverride =
        importType === ImportType.DailySalesHistoricalManualOverride;

    const { data } = useQuery(GET_IMPORT_STATUS, {
        variables: {
            id: logId,
        },
        skip: !logId || isHistoricalOverride,
        pollInterval: 1000,
    });

    const [getUploadUrl] = useMutation<GetUploadUrlResponse>(GET_UPLOAD_URL);

    const [processManualImport] =
        useMutation<ProcessImportResponse>(PROCESS_IMPORT);

    const processImportData = useCallback(
        async (file: File, date?: Date) => {
            const { data: uploadUrlResponse } = await getUploadUrl({
                variables: {
                    input: {
                        fileName: file.name,
                        importType,
                        ...(label ? { label } : {}),
                    },
                },
            });

            if (!uploadUrlResponse) {
                return undefined;
            }

            await uploadFile(
                file,
                uploadUrlResponse.getManualImportUploadUrl.url,
            );

            const processInput = {
                key: uploadUrlResponse.getManualImportUploadUrl.key,
                importType,
                ...(label ? { label } : {}),
            };

            let processImportResponse;
            if (date) {
                processImportResponse = await processManualImport({
                    variables: {
                        input: {
                            ...processInput,
                            targetDate: dayjs(date).format('YYYY-MM-DD'),
                        },
                    },
                });
            } else {
                processImportResponse = await processManualImport({
                    variables: {
                        input: processInput,
                    },
                });
            }

            if (!processImportResponse?.data?.processManualImport) {
                return undefined;
            }

            return processImportResponse.data.processManualImport;
        },
        [getUploadUrl, importType, label, processManualImport],
    );

    useEffect(() => {
        if (!logId) {
            return;
        }

        if (isHistoricalOverride) {
            return;
        }

        const log = data?.getManualImportLogById as ImportLogResult | undefined;
        if (!log) {
            return;
        }

        const clearUploadState = () => {
            setIsProcessing(false);
            setLogId(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        };

        if (log.status === 'Completed') {
            notifications.show({
                title: 'Success',
                message: 'File uploaded successfully',
                color: 'green',
            });
            onUploadComplete?.();
            clearUploadState();
        } else if (log.status === 'Failed') {
            notifications.show({
                title: 'Error',
                message:
                    'File upload failed. See error details in upload history.',
                color: 'red',
            });
            onUploadComplete?.();
            clearUploadState();
        }
    }, [data, isHistoricalOverride, logId, onUploadComplete]);

    const handleDateSubmit = async (date: Date) => {
        if (!selectedFile) return;

        try {
            setIsProcessing(true);
            const importResponse = await processImportData(selectedFile, date);

            if (importResponse) {
                setLogId(importResponse.logId);
            } else {
                setIsProcessing(false);
                onUploadComplete?.();
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message:
                    error instanceof Error
                        ? `Failed to upload file: ${error.message}`
                        : 'Failed to upload file. Please try again.',
                color: 'red',
            });
            setIsProcessing(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            onUploadComplete?.();
        } finally {
            setSelectedFile(null);
            setIsDateModalOpen(false);
        }
    };

    const handleFileChange = async (file: File) => {
        if (!file) return;

        if (importType === ImportType.DailySales) {
            setSelectedFile(file);
            setIsDateModalOpen(true);
            return;
        }

        try {
            setIsProcessing(true);
            const importResponse = await processImportData(file);

            if (importResponse) {
                if (isHistoricalOverride) {
                    notifications.show({
                        title: 'Upload scheduled',
                        message:
                            'Your file will be processed in the next nightly window (00:00 SAST).',
                        color: 'blue',
                    });
                    setIsProcessing(false);
                    setLogId(null);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                    onScheduled?.();
                } else {
                    setLogId(importResponse.logId);
                }
            } else {
                setIsProcessing(false);
                onUploadComplete?.();
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message:
                    error instanceof Error
                        ? `Failed to upload file: ${error.message}`
                        : 'Failed to upload file. Please try again.',
                color: 'red',
            });
            setIsProcessing(false);
            onUploadComplete?.();
        }
    };

    const handleDragEvents = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) {
            return;
        }

        if (e.type === 'dragover') {
            setIsDragging(true);
        } else if (e.type === 'dragleave') {
            setIsDragging(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (disabled) {
            return;
        }

        const file = e.dataTransfer.files[0];
        if (
            file &&
            fileTypes.includes(file.name.split('.').pop()?.toUpperCase() || '')
        ) {
            handleFileChange(file);
        } else {
            notifications.show({
                title: 'Invalid File',
                message: 'Please upload a valid Excel or CSV file',
                color: 'red',
            });
        }
    };

    const handleClick = () => {
        if (disabled) {
            return;
        }
        fileInputRef.current?.click();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileChange(file);
        }
    };

    const handleModalClose = () => {
        setIsDateModalOpen(false);
        setSelectedFile(null);
    };

    let dropzoneBody: JSX.Element;
    if (isProcessing) {
        dropzoneBody = (
            <div className="flex flex-col items-center justify-center">
                <Loader size="sm" className="mb-2" />
                <Text>Uploading file…</Text>
            </div>
        );
    } else if (disabled) {
        dropzoneBody = (
            <div>
                <Text size="lg" fw={500} className="mb-2">
                    Upload disabled
                </Text>
                <Text size="sm" color="dimmed">
                    {disabledReason ||
                        'A historical import is currently being processed.'}
                </Text>
            </div>
        );
    } else {
        dropzoneBody = (
            <div>
                <Text size="lg" fw={500} className="mb-2">
                    {title}
                </Text>
                <Text size="sm" color="dimmed">
                    {description}
                </Text>
            </div>
        );
    }

    const dropzoneClassName = [
        'relative border-2 border-dashed rounded-lg p-8 text-center transition-colors',
        disabled
            ? 'cursor-not-allowed opacity-60 border-gray-300 bg-gray-50'
            : 'cursor-pointer',
        !disabled && isDragging ? 'border-blue-500 bg-blue-50' : '',
        !disabled && !isDragging ? 'border-gray-300 hover:border-gray-400' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <>
            <Box
                className={dropzoneClassName}
                onDragOver={handleDragEvents}
                onDragLeave={handleDragEvents}
                onDrop={handleDrop}
                onClick={handleClick}
                aria-disabled={disabled}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled}
                />
                {dropzoneBody}
            </Box>
            {importType === ImportType.DailySales && (
                <DateSelectionModal
                    opened={isDateModalOpen}
                    onClose={handleModalClose}
                    onSubmit={handleDateSubmit}
                />
            )}
        </>
    );
}
